import { Input } from '@/common/components/Input.tsx'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { SubmitHandler, useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom';
import { AppRoutePaths } from '@/routing/route_paths'
import { Label } from '@/shad/ui/label'
import { useSelector, useDispatch } from "react-redux"
import { RootState } from '@/storage/store'
import { setReimbursementAccountDetail } from '@/storage/applications/reimubrusementAccountSlice'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout'
import { SubmitButton } from '@/app/pages/family_application/student_details/shared/submit-button'
import { DatePicker } from '@/app/common/components/DatePicker'
import { format, Locale } from 'date-fns'
import { enUS, es } from 'date-fns/locale'
import { useState } from 'react'
import { YYYYMMDDToJsDate } from '@/app/utils/date'
import { Button } from '@/app/common/components/Button'
import { BankAccountTypes } from '../constants'

const detailSchema = z
  .object({
    holders_name: z.string().min(1, 'holders_name_is_required'),
    account_type: z.number().min(1, 'account_type_is_required'),
    date_of_birth: z.date({
      required_error: 'account_holder_birth_date_is_required',
    })
  })

type FormValues = z.infer<typeof detailSchema>
function getLocale(string: string): Locale {
  if (string === 'es') {
    return es
  }
  return enUS
}
export const ReimbursementAccountDetail = () => {
  const { t: translate, i18n } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { currentAccount } = useSelector((state: RootState) => state.ReimbursementAccount)
  const [open, setIsOpen] = useState<boolean>(false)
  const currentLanguage = i18n.language

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(detailSchema),
    defaultValues: {
      holders_name: currentAccount?.accountDetail?.holders_name ?? "",
      account_type: currentAccount?.accountDetail?.account_type ?? undefined,
      date_of_birth: currentAccount?.accountDetail?.birth_date
        ? YYYYMMDDToJsDate(currentAccount?.accountDetail.birth_date)
        : undefined
    }
  })

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    dispatch(setReimbursementAccountDetail({ holders_name: data.holders_name, account_type: data.account_type, birth_date: format(data.date_of_birth, 'yyyy-MM-dd') }))
    navigate(AppRoutePaths.reimbursements.accounts.link.nickname)
  }

  const onBack = () => {
    navigate(-1)
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('we_need_little_more_info')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="w-80">
        <form
          id="form"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-3"
        >
          <div className="my-10">
            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="holders_name_label"
            >
              <Label
                id="holders_name_label"
                htmlFor="holders_name"
              >
                {translate('holders_name')}{' '}
                <span className="text-danger ml-1">*</span>
              </Label>
              <div className="relative mt-2 rounded-md shadow-sm">
                <Input
                  id="holders_name"
                  {...register('holders_name')}
                />
              </div>
              {errors.holders_name && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.holders_name.message ?? '')}
                </p>
              )}
            </section>
            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="date_of_birth_label"
            >
              <Label
                id="date_of_birth_label"
                htmlFor="date_of_birth"
              >
                {translate('Date_of_birth')}{' '}
                <span className="text-danger ml-1">*</span>
              </Label>
              <div className="relative mt-2 rounded-md shadow-sm">
                <Controller
                  control={control}
                  name="date_of_birth"
                  render={({ field }) => (
                    <DatePicker
                      fromDate={new Date('1920-01-01')}
                      toDate={new Date()}
                      captionLayout="dropdown"
                      open={open}
                      onOpenChange={setIsOpen}
                      locale={getLocale(currentLanguage)}
                      className="w-full"
                      selected={field.value}
                      defaultMonth={field.value}
                      mode="single"
                      onSelect={(date) => {
                        field.onChange(date)
                        if (date) {
                          setIsOpen(false)
                        }
                      }}
                    />
                  )}
                />
              </div>
              {errors.date_of_birth && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.date_of_birth.message ?? '')}
                </p>
              )}
            </section>
            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="account_type_label"
            >
              <Label
                id="account_type_label"
                htmlFor="account_type"
              >
                {translate('account_type')}{' '}
                <span className="text-danger ml-1">*</span>
              </Label>
              <div className="relative mt-2 rounded-md shadow-sm">
                <select
                  {...register('account_type', { valueAsNumber: true })}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                >
                  <option className="hidden" value="">
                    {translate('Select account type')}
                  </option>
                  {/* <Select
                  {...register('account_type')}
                  onValueChange={(value) => {
                    setValue('account_type', value)
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  <SelectContent> */}
                  {BankAccountTypes.map((account) => (
                    <option value={account.value} key={account.value}>
                      {account.label}
                    </option>
                  ))}
                  {/* </SelectContent>
                </Select> */}
                </select>
              </div>
              {errors.account_type && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.account_type.message ?? '')}
                </p>
              )}
            </section>
          </div>
        </form>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar className="justify-center">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onBack}
            className="px-10"
          >
            {translate('back')}
          </Button>
          {' '}          <SubmitButton
            form="form"
            status={'idle'}
            buttonTxt={translate('next')}
            className="px-10"
          />
        </div>
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
